import { ChangeDetectionStrategy, Component, computed, effect, input } from '@angular/core'
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'
import { map, merge, of, switchMap } from 'rxjs'
import { FormGroup, ReactiveFormsModule } from '@angular/forms'
import { FreetextSection } from '@features/certificate/models/certificate-shared.model'
import { FinalProvisionsForm } from '@features/certificate/models/final-provisions/final-provisions.model'
import { CertificateBaseComponent } from '@features/certificate/components/certificate-base/certificate-base.component'
import { AlbertInputWrapper } from '@features/certificate/components/certificate-input-wrapper/albert-input-wrapper'
import { AlbertSelectComponent } from '@shared/components/albert-select/albert-select.component'
import { AlbertCheckboxComponent } from '@shared/components/albert-checkbox/albert-checkbox.component'
import { AlbertInputComponent } from '@shared/components/albert-input/albert-input.component'
import { CertificateFreetextSectionComponent } from '@features/certificate/components/certificate-freetext-section/certificate-freetext-section.component'
import { freetextSection, setupFreetextSections } from '@features/certificate/utils/freetext-utils'
import { EXCLUSION_LIABILITY_OPTIONS } from '@features/certificate/models/certificate-select-options.model'
import { FINAL_PROVISIONS_VISIBILITY } from '@features/certificate/components/final-provisions/final-provisions.visibility'
import { CertificateCategory } from '@app/shared/models/certificate-category.model'
import { IfCategoryDirective } from '@features/certificate/directives/if-category.directive'
import { syncFormCategoryState } from '@core/utils/form-utils'

type FreetextSectionKey =
  | 'exclNeglWilfulMisconduct'
  | 'freetext'

@Component({
  selector: 'albert-final-provisions',
  imports: [ReactiveFormsModule,
    AlbertInputWrapper,
    AlbertSelectComponent,
    AlbertCheckboxComponent,
    AlbertInputComponent,
    CertificateFreetextSectionComponent,
    IfCategoryDirective
  ],
  templateUrl: './final-provisions.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FinalProvisionsComponent extends CertificateBaseComponent {
  public readonly form = input.required<FormGroup<FinalProvisionsForm>>()
  public readonly exclusionLiabilityOptions = EXCLUSION_LIABILITY_OPTIONS
  public readonly freetextSections: FreetextSection<FreetextSectionKey>[] = [
    freetextSection('exclNeglWilfulMisconduct', 'excl. negligence / wilful misconduct Freetext', 'excl-negl-wilful-misconduct-freetext'),
    freetextSection('freetext', 'Freetext Final Provisions', 'freetext')
  ]
  public readonly visibility = FINAL_PROVISIONS_VISIBILITY
  public readonly CertificateCategory = CertificateCategory

  public readonly freetextSectionsByKey: Record<FreetextSectionKey, FreetextSection> =
    setupFreetextSections(this.form, this.freetextSections)

  constructor() {
    super()
    effect(() => {
      const category = this.categoryService.activeCertificateCategory()
      syncFormCategoryState(this.form(), category, this.visibility)
    })

    toObservable(computed(() => ({ form: this.form(), canEdit: this.canEdit() }))).pipe(
      switchMap(state => merge(
        of(false),
        state.form.controls.airBrokerIncluded.valueChanges.pipe(map(value => value === true))
      ).pipe(map(ticked => ({ ...state, ticked })))),
      takeUntilDestroyed()
    ).subscribe(({ form, canEdit, ticked }) => {
      if (canEdit && form.controls.airBrokerIncluded.value === true) {
        form.controls.airBrokerName.enable()
      } else {
        form.controls.airBrokerName.disable()
      }
      if (ticked) {
        form.controls.airBrokerName.markAsTouched()
      }
    })
  }
}
